package com.swetdot.swetgram.ui.groups

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.swetdot.swetgram.domain.GroupJoinDecision
import com.swetdot.swetgram.domain.GroupJoinRequestEntry
import com.swetdot.swetgram.domain.GroupJoinRequestId
import com.swetdot.swetgram.domain.LocalDay
import com.swetdot.swetgram.ui.chat.render
import com.swetdot.swetgram.ui.design.SwetEmptyState
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetListRowSkeleton
import com.swetdot.swetgram.ui.design.SwetTextButton
import com.swetdot.swetgram.ui.design.SwetTopAppBar
import com.swetdot.swetgram.ui.profile.ProfileConfirmDialog
import kotlinx.datetime.TimeZone

/**
 * A private group's approval queue.
 *
 * NO DESIGN EXISTS FOR THIS SCREEN. SDS §14 covers a group card and a group identity block; SPS
 * §12.6's Members tab does not mention approvals; STA §13.5 has no join-request endpoint at all.
 * Everything below is therefore a DECISION built from components that already exist, and the four
 * states SDS §25 asks of every surface -- first frame, empty, error, loaded -- are the shape it is
 * built on.
 *
 *
 * WHY BOTH ACTIONS ARE CONFIRMED
 *
 * Approving puts a stranger into a private group's chat and rejecting turns a person away, and both
 * are taken on a surface other admins are also looking at. A queue is exactly where a mis-tap is
 * likeliest -- the rows are near-identical and the buttons are small -- so the confirmation names
 * the person and the decision before either happens. Nothing is applied optimistically: a row
 * leaves the list when the server has answered and not before.
 *
 * ONE DECISION AT A TIME. While one is in flight the others are disabled rather than absent, which
 * is the one place on this surface where SDS §25.1's disabled state is right: it IS momentary.
 *
 *
 * WHY THE ROW'S BUTTONS ARE NOT RED
 *
 * `SwetDestructiveTextButton` is `fillMaxWidth` by construction -- it is built for the bottom of a
 * settings list -- so it cannot sit in a row's trailing slot, and adding a width-bounded variant
 * would be inventing a component to solve a layout problem. SDS §22.2 requires that status never
 * ride on colour alone anyway: "Reject" says what it does. The destructive weight lands where the
 * SDS actually puts it, on the filled danger button inside the confirmation.
 */
@Composable
fun GroupJoinRequestsScreen(
    state: GroupJoinRequestsUiState,
    onBack: () -> Unit,
    /** `resolveGroupJoinRequest`. The ViewModel re-checks the permission before sending it. */
    onResolve: (GroupJoinRequestId, GroupJoinDecision) -> Unit,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
    onDismissError: (() -> Unit)? = null,
    /** Null disables paging -- for a caller that shows a single page on purpose. */
    onLoadMore: (() -> Unit)? = null,
    zone: TimeZone = TimeZone.currentSystemDefault(),
) {
    // The request AND the decision together: re-deriving the decision inside the dialog would let a
    // background refresh change what the user agreed to between the tap and the confirmation.
    var pending by remember { mutableStateOf<PendingDecision?>(null) }
    val visible = state.visible

    Column(modifier.fillMaxSize()) {
        // TODO(localization): every user-facing string in this file moves into resources (EN + RU).
        SwetTopAppBar(
            title = "Join requests",
            // No count. `Group` carries none, and counting the rows that happen to be loaded would
            // say "20 requests" about a queue of 200 and then change as the admin scrolled.
            onBack = onBack,
        )

        // Over the list, never in place of it: a failed page must not take away the rows that did
        // load, and on this screen those rows are the only way to answer anybody.
        if (state.error != null) {
            SwetErrorBanner(message = state.error, onRetry = onRetry, onDismiss = onDismissError)
        }

        when {
            // First load. Skeleton rows rather than a spinner (SDS §2.1).
            state.requests.isEmpty() && !state.loadedOnce -> Column(Modifier.fillMaxSize()) {
                repeat(SKELETON_ROWS) { SwetListRowSkeleton() }
            }

            // §17.1's voice: short, helpful, calm. The second line says where these come from,
            // because an admin who has never seen one has no idea what fills this list.
            visible.isEmpty() -> SwetEmptyState(
                title = "No join requests.",
                body = "People who open this group's invite link and ask to join appear here.",
                actionText = onRetry?.let { "Refresh" },
                onAction = onRetry,
            )

            else -> LazyColumn(Modifier.fillMaxSize().navigationBarsPadding()) {
                items(
                    items = visible,
                    // The request id, not the position: answering one takes a row out of the
                    // middle, and a positional key would animate the wrong one away.
                    key = { it.id.value },
                ) { request ->
                    JoinRequestRow(
                        request = request,
                        // Disabled, not hidden, while any decision is in flight: this one is
                        // genuinely momentary, which is the case SDS §25.1's disabled state is for.
                        enabled = state.canResolve && state.resolving == null,
                        zone = zone,
                        onApprove = { pending = PendingDecision(request, GroupJoinDecision.APPROVE) },
                        onReject = { pending = PendingDecision(request, GroupJoinDecision.REJECT) },
                    )
                }

                if (state.hasMore) {
                    item(key = PAGING_FOOTER_KEY) {
                        // Keyed on the item count so it fires once per page rather than on every
                        // recomposition -- and NOT again after a failed page, since the count did
                        // not change. The banner's Retry is the way back.
                        LaunchedEffect(state.requests.size) { onLoadMore?.invoke() }
                        SwetListRowSkeleton()
                    }
                }
            }
        }
    }

    pending?.let { decision ->
        val name = decision.request.user.displayName
        val approving = decision.decision == GroupJoinDecision.APPROVE
        ProfileConfirmDialog(
            title = if (approving) "Let $name join?" else "Turn down $name?",
            // Each body names the consequence the admin cannot otherwise know. For an approval it
            // is DECISIONS §7's history rule -- the new member starts from now, not from the
            // beginning. For a rejection it is that this is not a ban: they can ask again.
            body = if (approving) {
                "They can read and post in this group's chat straight away. They will not see " +
                    "anything sent before now."
            } else {
                "They are not added to the group. They can ask again with the invite link; ban " +
                    "them from the members list if you want that to stop."
            },
            confirmText = if (approving) "Approve" else "Reject",
            onConfirm = {
                pending = null
                onResolve(decision.request.id, decision.decision)
            },
            onDismiss = { pending = null },
            // A rejection removes nothing that exists and can be undone by the person simply
            // asking again, so it is not painted as a deletion. An approval is not destructive
            // either -- but both are still confirmed. See the file header.
            destructive = false,
            busy = state.resolving != null,
        )
    }
}

/** A decision the admin has been asked to confirm, with the request it belongs to. */
private data class PendingDecision(
    val request: GroupJoinRequestEntry,
    val decision: GroupJoinDecision,
)

/** Enough placeholder rows to fill the first screen without pretending to know the real count. */
private const val SKELETON_ROWS = 5
private const val PAGING_FOOTER_KEY = "group-join-requests-paging-footer"

/**
 * One pending request: who is asking, when they asked, and the two answers.
 *
 * The applicant's name is NOT a link to their profile, and that is a deliberate narrowing rather
 * than something left out. `GroupJoinRequest.user` is a `UserPublic`, so the username is there and
 * a profile route would work -- but this screen's whole job is a yes-or-no decision, and a tap that
 * navigates away sitting between two small buttons is the tap people make by accident. An admin who
 * wants to check somebody can search for them.
 */
@Composable
private fun JoinRequestRow(
    request: GroupJoinRequestEntry,
    enabled: Boolean,
    zone: TimeZone,
    onApprove: () -> Unit,
    onReject: () -> Unit,
) {
    GroupPersonRow(
        user = request.user,
        // Rendered with the same day formatter the timeline, the blocked list and the member card
        // use, so a date never reads one way on one screen and another way on the next. Null when
        // the date could not be parsed -- the row still has to be shown, because it is the only
        // place this request can be answered.
        detail = request.createdAt?.let { "Asked on ${LocalDay.of(it, zone).render()}" },
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            SwetTextButton(text = "Approve", onClick = onApprove, enabled = enabled)
            SwetTextButton(text = "Reject", onClick = onReject, enabled = enabled)
        }
    }
}
